import {useEffect, useState} from 'react';
import {Box, CircularProgress, Paper, TableContainer} from '@mui/material';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import {TaxHeader} from '@/pages/components/tax-computation/sub';
import {TaxMainBasic, TaxMainTypeA, TaxMainTypeB, TaxMainTypeC} from '@/pages/components/tax-computation/main';
import type {TaxComputationResult, TaxExtraItem} from '@/services/auditService/interface/tax-com';
import {TaxComputationType} from '@/services/auditService/interface/tax-com';
import {normalizeTaxComputationType} from '@/pages/components/tax-computation/taxComP1Text';

interface TaxMainProps {
    annualId: number;
    taxInfo?: {
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null;
    taxMainData?: TaxComputationResult | null;
    taxType?: TaxComputationType | null;
    taxPayment?: number;
    taxPaymentChange?: (taxPayment: number) => void;
    taxLossBf?: number;
    taxLossBfChange?: (taxLossBf: number) => void;
    p1Note?: string | null;
    p1Confirm?: string | null;
    p1NoteChange?: (note: string) => void;
    p1ConfirmChange?: (confirm: string) => void;
    onP1TextBlur?: () => void;
    editableExtras?: boolean;
    extraAddItems?: TaxExtraItem[];
    extraLessItems?: TaxExtraItem[];
    onExtraAddChange?: (items: TaxExtraItem[]) => void;
    onExtraLessChange?: (items: TaxExtraItem[]) => void;
}

export function TaxMain({
                            annualId,
                            taxInfo: taxInfoProp,
                            taxMainData: taxMainDataProp,
                            taxType: taxTypeProp,
                            taxPayment,
                            taxPaymentChange,
                            taxLossBf,
                            taxLossBfChange,
                            p1Note,
                            p1Confirm,
                            p1NoteChange,
                            p1ConfirmChange,
                            onP1TextBlur,
                            editableExtras = false,
                            extraAddItems,
                            extraLessItems,
                            onExtraAddChange,
                            onExtraLessChange,
                        }: TaxMainProps) {
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);

    const initialize = useTaxStore((state) => state.initialize);
    const getTaxMainData = useTaxStore((state) => state.getTaxComputationResult);
    const getTaxType = useTaxStore((state) => state.getTaxType);

    const isControlled = taxInfoProp !== undefined || taxMainDataProp !== undefined || taxTypeProp !== undefined;

    const [loading, setLoading] = useState(!isControlled);
    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);

    const [taxMainData, setTaxMainData] = useState<TaxComputationResult | null>(null);
    const [taxType, setTaxType] = useState<TaxComputationType | null>(null);

    useEffect(() => {
        if (isControlled) {
            return;
        }
        const loadData = async () => {
            setLoading(true);
            try {
                const info = await getTaxComputationInfo(annualId);
                setTaxInfo(info);

                const companyDetails = await getCompanyDetailsInfo(annualId);
                const companyId = companyDetails?.companyId;

                if (companyId) {
                    await initialize(companyId, annualId);

                    const mainData = getTaxMainData();
                    setTaxMainData(mainData);
                    setTaxType(getTaxType());
                } else {
                    setTaxMainData(null);
                    setTaxType(null);
                }
            } catch (error) {
                setTaxInfo(null);
                setTaxMainData(null);
                setTaxType(null);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [annualId, getCompanyDetailsInfo, getTaxComputationInfo, getTaxMainData, getTaxType, initialize, isControlled]);

    const effectiveTaxInfo = isControlled ? (taxInfoProp ?? null) : taxInfo;
    const effectiveTaxMainData = isControlled ? (taxMainDataProp ?? null) : taxMainData;
    const effectiveTaxType = normalizeTaxComputationType(isControlled ? (taxTypeProp ?? null) : taxType);
    const effectiveLoading = isControlled ? false : loading;

    if (effectiveLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!effectiveTaxInfo) {
        return <Box sx={{p: 2}}>Failed to load data</Box>;
    }

    return (
        <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
            <TaxHeader
                companyName={effectiveTaxInfo.companyName}
                fileNo={effectiveTaxInfo.taxFileNo || ''}
                yearOfAssessment={effectiveTaxInfo.yearOfAssessment}
            />

            <TableContainer component={Paper}>
                <TaxMainBasic
                    data={effectiveTaxMainData}
                    currency={effectiveTaxInfo.presentationCurrency}
                    editableExtras={editableExtras}
                    extraAddItems={extraAddItems}
                    extraLessItems={extraLessItems}
                    onExtraAddChange={onExtraAddChange}
                    onExtraLessChange={onExtraLessChange}
                />

                {effectiveTaxType === TaxComputationType.TYPE_A ? (
                    <TaxMainTypeA
                        data={effectiveTaxMainData?.profitsTaxA}
                        lessTotal={effectiveTaxMainData?.lessTotal}
                        currency={effectiveTaxInfo.presentationCurrency}
                        taxLossBf={taxLossBf}
                        taxLossBfChange={taxLossBfChange}
                        note={p1Note}
                        confirm={p1Confirm}
                        noteChange={p1NoteChange}
                        confirmChange={p1ConfirmChange}
                        onTextBlur={onP1TextBlur}
                    />
                ) : null}

                {effectiveTaxType === TaxComputationType.TYPE_B ? (
                    <TaxMainTypeB
                        data={effectiveTaxMainData?.profitsTaxB}
                        currency={effectiveTaxInfo.presentationCurrency}
                        taxPayment={taxPayment}
                        taxPaymentChange={taxPaymentChange}
                        taxLossBf={taxLossBf}
                        taxLossBfChange={taxLossBfChange}
                        note={p1Note}
                        confirm={p1Confirm}
                        noteChange={p1NoteChange}
                        confirmChange={p1ConfirmChange}
                        onTextBlur={onP1TextBlur}
                    />
                ) : null}

                {effectiveTaxType === TaxComputationType.TYPE_C ? (
                    <TaxMainTypeC
                        data={effectiveTaxMainData?.profitsTaxC}
                        currency={effectiveTaxInfo.presentationCurrency}
                        taxPayment={taxPayment}
                        taxPaymentChange={taxPaymentChange}
                        taxLossBf={taxLossBf}
                        taxLossBfChange={taxLossBfChange}
                        note={p1Note}
                        confirm={p1Confirm}
                        noteChange={p1NoteChange}
                        confirmChange={p1ConfirmChange}
                        onTextBlur={onP1TextBlur}
                    />
                ) : null}
            </TableContainer>
        </Box>
    );
}
